import { useState, useEffect, useCallback } from 'react';
import { apiClient, useAuth } from '../contexts/AuthContext';
import { BottomNav } from '../components/BottomNav';
import { Button } from '../components/ui/button';
import {
  UsersRound, Shuffle, ChevronLeft, ChevronRight, Dumbbell, Crown, Clock, ArrowRightLeft, AlertCircle, Lock, Unlock, UserX, UserCheck
} from 'lucide-react';
import { toast } from 'sonner';
import { getWeekStartTahiti, formatWeekRange } from '../utils/tahiti';

const PROGRAM_COLORS = {
  Dos: '#FF6B35',
  Pec: '#3b82f6',
  Jambes: '#ef4444',
  'Épaules': '#f59e0b',
  Bras: '#8b5cf6'
};

export default function GroupesPage({ embedded = false }) {
  const { user } = useAuth();
  const [weekOffset, setWeekOffset] = useState(0);
  const [groupData, setGroupData] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [generateError, setGenerateError] = useState(null);
  const [movingMember, setMovingMember] = useState(null);
  const [schedulerInfo, setSchedulerInfo] = useState(null);

  const weekStart = getWeekStartTahiti(weekOffset);
  const isAdmin = user?.role === 'admin';

  const fetchGroups = useCallback(async () => {
    setLoading(true);
    try {
      const ws = getWeekStartTahiti(weekOffset);
      const promises = [
        apiClient.get(`/groups/current?week_start=${ws}`),
        apiClient.get('/groups/history?limit=5')
      ];
      if (user?.role === 'admin') {
        promises.push(apiClient.get('/scheduler/status'));
      }
      const results = await Promise.all(promises);
      setGroupData(results[0].data);
      setHistory(results[1].data);
      if (results[2]) setSchedulerInfo(results[2].data);
    } catch {
      toast.error('Erreur chargement groupes');
    } finally {
      setLoading(false);
    }
  }, [user?.role, weekOffset]);

  useEffect(() => { fetchGroups(); }, [fetchGroups]);

  const handleGenerate = async () => {
    setGenerating(true);
    setGenerateError(null);
    try {
      const res = await apiClient.post('/groups/generate', { week_start: weekStart });
      // Use the response directly — do NOT call fetchGroups which could overwrite
      const data = res.data;
      if (data && data.groups) {
        data.generated = true;
        setGroupData(data);
        toast.success(`Groupes generes ! ${data.total_members || ''} membres repartis`);
      } else {
        toast.success('Groupes generes !');
      }
      // Only refresh history separately, don't touch groupData
      try {
        const histRes = await apiClient.get('/groups/history?limit=5');
        setHistory(histRes.data);
      } catch {}
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Erreur lors de la generation';
      setGenerateError(msg);
      toast.error(msg);
    } finally {
      setGenerating(false);
    }
  };

  const handleMoveMember = async (targetGroup) => {
    if (!movingMember) return;
    try {
      await apiClient.put('/groups/move-member', {
        member_id: movingMember.member_id,
        target_group: targetGroup,
        week_start: groupData?.week_start || weekStart
      });
      toast.success(`${movingMember.name} deplace dans le groupe ${targetGroup}`);
      setMovingMember(null);
      fetchGroups();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const handleAssignOmega = async (memberId, targetGroup) => {
    try {
      await apiClient.put('/groups/assign-omega', {
        member_id: memberId,
        target_group: targetGroup,
        week_start: groupData?.week_start || weekStart
      });
      toast.success('Membre assigne !');
      fetchGroups();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const handleToggleAbsence = async (m) => {
    try {
      const res = await apiClient.put('/groups/member-absence', {
        member_id: m.user_id,
        absent: !m.absent,
        week_start: groupData?.week_start || weekStart
      });
      toast.success(res.data?.message || 'Statut mis a jour');
      fetchGroups();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const handleToggleLock = async () => {
    const newLocked = !groupData?.locked;
    try {
      await apiClient.put('/groups/lock', { week_start: weekStart, locked: newLocked });
      setGroupData(prev => ({ ...prev, locked: newLocked }));
      toast.success(newLocked ? 'Groupes verrouilles — pas de redispatch auto samedi' : 'Groupes deverrouilles — redispatch auto samedi actif');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const formatWeek = (ws) => formatWeekRange(ws);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0F0F0F] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#FF6B35] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const groups = groupData?.groups || [];
  const isGenerated = groupData?.generated;

  return (
    <div className={`${embedded ? '' : 'min-h-screen'} bg-[#0F0F0F] ${embedded ? 'pb-4' : 'pb-20'}`}>
      {!embedded && (
      <header className="sticky top-0 z-40 bg-[#0F0F0F]/95 backdrop-blur-sm border-b border-[#2A2A2E]">
        <div className="flex items-center justify-between px-4 py-3">
          <div>
            <p className="text-xs text-[#FF6B35] uppercase tracking-widest">Module A</p>
            <h1 className="text-xl font-bold text-white">Groupes de la semaine</h1>
          </div>
          {isAdmin && (
            <Button
              onClick={handleGenerate}
              disabled={generating}
              data-testid="generate-groups-btn"
              className="bg-[#FF6B35] text-black hover:bg-[#E85A25]"
              size="sm"
            >
              {generating ? <Clock className="w-4 h-4 mr-1 animate-spin" /> : <Shuffle className="w-4 h-4 mr-1" />}
              {generating ? 'Generation...' : 'Generer'}
            </Button>
          )}
        </div>
      </header>
      )}

      <main className="p-4 space-y-4 animate-fade-in">
        {/* Admin Dispatch Button */}
        {isAdmin && (
          <div className={`rounded-sm p-4 ${isGenerated ? 'bg-[#1B1B1B] border border-[#2A2A2E]' : 'bg-[#FF6B35]/10 border border-[#FF6B35]/30'}`} data-testid="dispatch-panel">
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm font-bold ${isGenerated ? 'text-zinc-300' : 'text-[#FF6B35]'}`}>
                  {isGenerated ? 'Groupes dispatches' : 'Groupes non dispatches'}
                </p>
                <p className="text-xs text-zinc-400 mt-0.5">
                  {isGenerated
                    ? `${groupData.total_members || 0} membres repartis${groupData.auto_generated ? ' (auto)' : ''}`
                    : 'Appuie pour dispatcher les membres en groupes'}
                </p>
              </div>
              <Button
                onClick={handleGenerate}
                disabled={generating}
                data-testid="generate-groups-btn"
                className={isGenerated
                  ? 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700 border border-zinc-700'
                  : 'bg-[#FF6B35] text-black hover:bg-[#E85A25]'}
                size="sm"
              >
                {generating ? <Clock className="w-4 h-4 mr-1 animate-spin" /> : <Shuffle className="w-4 h-4 mr-1" />}
                {generating ? 'Dispatch...' : isGenerated ? 'Redispatcher' : 'Dispatcher les groupes'}
              </Button>
            </div>
            {/* Lock toggle */}
            {isGenerated && (
              <div className="mt-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {groupData?.locked ? (
                    <Lock className="w-4 h-4 text-[#FF6B35]" />
                  ) : (
                    <Unlock className="w-4 h-4 text-zinc-500" />
                  )}
                  <span className={`text-xs ${groupData?.locked ? 'text-[#FF6B35] font-semibold' : 'text-zinc-500'}`}>
                    {groupData?.locked ? 'Verrouille — pas de redispatch auto' : 'Non verrouille — redispatch auto samedi 22h'}
                  </span>
                </div>
                <button
                  onClick={handleToggleLock}
                  className={`px-3 py-1.5 rounded-sm text-xs font-medium transition-all ${
                    groupData?.locked
                      ? 'bg-[#FF6B35] text-black hover:bg-[#E85A25]'
                      : 'bg-zinc-800 text-zinc-400 border border-zinc-700 hover:border-[#FF6B35] hover:text-[#FF6B35]'
                  }`}
                  data-testid="toggle-lock-btn"
                >
                  {groupData?.locked ? 'Deverrouiller' : 'Verrouiller'}
                </button>
              </div>
            )}
            {generateError && (
              <div className="mt-3 flex items-start gap-2 bg-red-500/10 border border-red-500/30 rounded-sm p-3" data-testid="generate-error">
                <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-red-400">{generateError}</p>
              </div>
            )}
          </div>
        )}

        {/* Week Nav */}
        <div className="flex items-center justify-between bg-[#1B1B1B] border border-[#2A2A2E] rounded-sm p-3">
          <button onClick={() => setWeekOffset(o => o - 1)} className="p-2 text-zinc-400 hover:text-white rounded-sm">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="text-center">
            <p className="text-white font-medium">{formatWeek(weekStart)}</p>
            {weekOffset === 0 && <span className="text-xs text-[#FF6B35]">Semaine actuelle</span>}
          </div>
          <button onClick={() => setWeekOffset(o => o + 1)} className="p-2 text-zinc-400 hover:text-white rounded-sm">
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>

        {/* Scheduler Info (Admin only) */}
        {isAdmin && schedulerInfo && (
          <div className="bg-[#0F0F0F] border border-[#2A2A2E] rounded-sm p-3 flex items-center gap-3" data-testid="scheduler-info">
            <Clock className="w-4 h-4 text-[#FF6B35] flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-xs text-zinc-400">
                Generation auto : <span className="text-white font-medium">{schedulerInfo.next_generation}</span>
              </p>
              <p className="text-xs text-zinc-400">
                {schedulerInfo.scheduler_active ? 'Scheduler actif' : 'Scheduler inactif'} &middot; Dans {schedulerInfo.hours_until_next}h
              </p>
            </div>
          </div>
        )}

        {/* Move Member Banner */}
        {movingMember && (
          <div className="bg-[#FF6B35]/10 border border-[#FF6B35]/30 rounded-sm p-3" data-testid="move-member-banner">
            <p className="text-sm text-[#FF6B35] mb-2">
              Deplacer <strong>{movingMember.name}</strong> vers quel groupe ?
            </p>
            <div className="flex gap-2 flex-wrap">
              {[1, 2, 3, 4, 5].filter(g => g !== movingMember.from_group).map(g => {
                const gData = groups.find(gr => gr.group_num === g);
                return (
                  <button
                    key={g}
                    onClick={() => handleMoveMember(g)}
                    className="px-3 py-1.5 bg-[#1B1B1B] border border-zinc-700 rounded-sm text-xs text-white hover:border-[#FF6B35] transition-colors"
                    data-testid={`move-to-group-${g}`}
                  >
                    Grp {g} - {gData?.group_name || gData?.program}
                  </button>
                );
              })}
              <button onClick={() => setMovingMember(null)} className="px-3 py-1.5 text-xs text-zinc-500 hover:text-white">
                Annuler
              </button>
            </div>
          </div>
        )}

        {/* Groups */}
        {isGenerated && groups.length > 0 ? (
          <div className="space-y-3" data-testid="groups-list">
            {(() => {
              const presentCounts = groups.map(g => (g.members || []).filter(m => !m.absent).length);
              const avgPresent = presentCounts.reduce((a, b) => a + b, 0) / (groups.length || 1);
              return groups.map((group) => {
              const color = PROGRAM_COLORS[group.program] || '#FF6B35';
              const DAYS = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi'];
              const dayLabel = DAYS[group.group_num - 1] || '';
              const present = (group.members || []).filter(m => !m.absent).length;
              const absents = (group.members || []).length - present;
              const delta = Math.round(present - avgPresent);
              return (
                <div
                  key={group.group_num}
                  className="bg-[#1B1B1B] border border-[#2A2A2E] rounded-sm overflow-hidden"
                  style={{ borderTopColor: color, borderTopWidth: '3px' }}
                  data-testid={`group-${group.group_num}`}
                >
                  <div className="flex items-center justify-between p-4">
                    <div className="flex items-center gap-3">
                      <div
                        className="w-10 h-10 rounded-sm flex items-center justify-center font-mono text-lg font-bold"
                        style={{ backgroundColor: `${color}20`, color }}
                      >
                        {group.group_num}
                      </div>
                      <div>
                        <h3 className="text-white font-bold">
                          {group.group_name || `Groupe ${group.group_num}`}
                        </h3>
                        <p className="text-xs text-zinc-500">
                          {dayLabel} &middot; {group.program} &middot; {present} present{present > 1 ? 's' : ''}
                          {absents > 0 && <span className="text-red-400"> &middot; {absents} absent{absents > 1 ? 's' : ''}</span>}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {delta <= -1 && (
                        <span className="text-[9px] font-bold px-2 py-1 rounded-sm bg-red-500/15 text-red-400" data-testid={`group-delta-${group.group_num}`}>
                          {delta} vs moy. ({avgPresent.toFixed(1)})
                        </span>
                      )}
                      {delta >= 1 && (
                        <span className="text-[9px] font-bold px-2 py-1 rounded-sm bg-[#FFB800]/15 text-[#FFB800]" data-testid={`group-delta-${group.group_num}`}>
                          +{delta} vs moy. ({avgPresent.toFixed(1)})
                        </span>
                      )}
                      <Dumbbell className="w-5 h-5" style={{ color }} />
                    </div>
                  </div>
                  {group.members.length > 0 && (
                    <div className="border-t border-[#2A2A2E] divide-y divide-zinc-800/50">
                      {group.members.map((m) => {
                        const tierCfg = m.tier ? {
                          alpha: { color: 'text-yellow-400', bg: 'bg-yellow-500/20 ring-1 ring-yellow-500/40', label: 'Alpha' },
                          beta: { color: 'text-blue-400', bg: 'bg-blue-500/20', label: 'Beta' }
                        }[m.tier] : null;
                        return (
                          <div key={m.user_id} className={`flex items-center gap-3 px-4 py-2.5 group ${m.absent ? 'opacity-50' : ''}`}>
                            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                              tierCfg?.bg || 'bg-zinc-800 text-zinc-400'
                            } ${tierCfg?.color || 'text-zinc-400'}`}>
                              {m.name?.charAt(0)?.toUpperCase() || '?'}
                            </div>
                            <span className={`text-sm flex-1 ${m.absent ? 'text-zinc-500 line-through' : 'text-zinc-300'}`}>{m.name}</span>
                            {m.absent && (
                              <span className="text-[9px] px-1.5 py-0.5 rounded-sm bg-red-500/15 text-red-400 font-bold" data-testid={`absent-badge-${m.user_id}`}>Absent</span>
                            )}
                            {tierCfg && (
                              <span className={`text-[9px] px-1.5 py-0.5 rounded-sm ${tierCfg.color} font-bold`}>{tierCfg.label}</span>
                            )}
                            {m.user_id === user?.id && (
                              <span className="text-[10px] px-2 py-0.5 bg-[#FF6B35]/20 text-[#FF6B35] rounded-sm font-bold">MOI</span>
                            )}
                            {isAdmin && (
                              <button
                                onClick={() => handleToggleAbsence(m)}
                                className={`p-1 rounded-sm hover:bg-zinc-700 transition-all ${m.absent ? 'text-green-500 hover:text-green-400' : 'text-zinc-500 hover:text-red-400'}`}
                                title={m.absent ? 'Marquer present' : 'Marquer absent'}
                                data-testid={`toggle-absence-${m.user_id}`}
                              >
                                {m.absent ? <UserCheck className="w-3.5 h-3.5" /> : <UserX className="w-3.5 h-3.5" />}
                              </button>
                            )}
                            {isAdmin && !movingMember && (
                              <button
                                onClick={() => setMovingMember({ member_id: m.user_id, name: m.name, from_group: group.group_num })}
                                className="p-1 rounded-sm hover:bg-zinc-700 text-zinc-500 hover:text-white transition-all"
                                title="Deplacer"
                                data-testid={`move-member-${m.user_id}`}
                              >
                                <ArrowRightLeft className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
              });
            })()}
          </div>
        ) : (
          <div className="bg-[#1B1B1B] border border-[#2A2A2E] rounded-sm p-8 text-center" data-testid="no-groups">
            <UsersRound className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
            <p className="text-zinc-400 mb-2">Pas encore de groupes cette semaine</p>
            <p className="text-sm text-zinc-500">
              {isAdmin
                ? 'Generez les groupes en cliquant sur le bouton ci-dessus'
                : "Le coach n'a pas encore genere les groupes"}
            </p>
          </div>
        )}

        {/* Unassigned Omega Members */}
        {isAdmin && isGenerated && (groupData?.unassigned_omega?.length > 0 || groupData?.unassigned_others?.length > 0) && (
          <div className="bg-[#1B1B1B] border border-[#2A2A2E] border-t-2 border-t-yellow-500 rounded-sm" data-testid="unassigned-omega">
            <div className="p-4 border-b border-[#2A2A2E]">
              <h3 className="text-sm font-bold text-yellow-400 flex items-center gap-2">
                <UsersRound className="w-4 h-4" />
                Membres non-assignes ({(groupData?.unassigned_omega?.length || 0) + (groupData?.unassigned_others?.length || 0)})
              </h3>
              <p className="text-xs text-zinc-500 mt-0.5">Cliquer pour assigner a un groupe</p>
            </div>
            <div className="divide-y divide-zinc-800/50">
              {[...(groupData?.unassigned_omega || []), ...(groupData?.unassigned_others || [])].map((m) => (
                <div key={m.user_id} className="p-3 px-4" data-testid={`unassigned-${m.user_id}`}>
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-7 h-7 rounded-full bg-yellow-500/20 text-yellow-400 flex items-center justify-center text-xs font-bold">
                      {m.name?.charAt(0)?.toUpperCase() || '?'}
                    </div>
                    <span className="text-sm text-zinc-300 flex-1">{m.name}</span>
                    {m.tier === 'omega' && <span className="text-[9px] text-yellow-400 font-bold px-1.5 py-0.5 rounded-sm bg-yellow-500/20">Omega</span>}
                  </div>
                  <div className="flex gap-1.5 flex-wrap">
                    {[1, 2, 3, 4, 5].map(g => {
                      const gData = groups.find(gr => gr.group_num === g);
                      return (
                        <button
                          key={g}
                          onClick={() => handleAssignOmega(m.user_id, g)}
                          className="px-2.5 py-1 bg-[#0F0F0F] border border-zinc-700 rounded-sm text-[10px] text-zinc-300 hover:border-[#FF6B35] hover:text-[#FF6B35] transition-colors"
                          data-testid={`assign-${m.user_id}-to-${g}`}
                        >
                          Grp {g} — {gData?.program || gData?.group_name || ''}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* History */}
        {history.length > 0 && (
          <div className="bg-[#1B1B1B] border border-[#2A2A2E] rounded-sm p-4" data-testid="groups-history">
            <h3 className="text-sm font-medium text-zinc-400 mb-3 flex items-center gap-2">
              <Crown className="w-4 h-4 text-[#FF6B35]" />
              Historique recent
            </h3>
            <div className="space-y-2">
              {history.slice(0, 4).map((h) => (
                <div key={h.week_start} className="flex items-center justify-between p-2 bg-[#0F0F0F] rounded-sm">
                  <span className="text-xs text-zinc-400">{formatWeek(h.week_start)}</span>
                  <div className="flex gap-1">
                    {(h.groups || []).map((g) => (
                      <span
                        key={g.group_num}
                        className="text-[10px] px-1.5 py-0.5 rounded-sm font-bold"
                        style={{ backgroundColor: `${PROGRAM_COLORS[g.program] || '#FF6B35'}20`, color: PROGRAM_COLORS[g.program] || '#FF6B35' }}
                      >
                        {g.group_name || g.program}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
      {!embedded && <BottomNav />}
    </div>
  );
}
